import { useEffect, useMemo, useRef, useState } from 'react';
import {
  getSolutions,
  getDateRange,
  getExpandedColumns,
  getExpandedRows,
  getFilteredTasks,
  getFilters,
  getGroupBy,
  getOrganizations,
  getReleases,
  getRowHeights,
  onDateRangeChange,
  onExpandedColumnsChange,
  onExpandedRowsChange,
  onFiltersChange,
  onGroupByChange,
  onRowHeightsChange,
  setDateRange,
  setFilters,
  setGroupBy,
  toggleColumn,
  toggleRow,
  getIsAuthenticated,
  onIsAuthenticatedChange,
  setIsAuthenticated,
  onTasksChange,
  onFilteredTasksChange,
  onSelectTaskChange,
  getSelectedTask,
  getSolutionVersions,
  getSolutionVersionsById,
} from '.'
import {
  Solution,
  DateRange,
  TaskCategory,
  Organization,
  Release,
  Task,
  SolutionVersion,
  Filter,
} from '../types';
import { groupBy } from '../utils/arrayUtils';

export const useIsTaskSelected = (task: Task): boolean => {
  const [isSelected, setIsSelected] = useState(() => {
    const selectedTask = getSelectedTask();
    return selectedTask && selectedTask.id === task.id;
  });
  const selectedRef = useRef(isSelected);

  useEffect(() => {
    return onSelectTaskChange((selectedTask) => {
      const nextIsSelected = selectedTask && selectedTask.id === task.id;
      if (nextIsSelected !== selectedRef.current) {
        setIsSelected(nextIsSelected);
        selectedRef.current = nextIsSelected;
      }
    });
  }, [task]);

  return isSelected;
};

export const useFilteredTasks = (): Task[] => {
  const [tasks, setTasks] = useState<Task[]>(getFilteredTasks);

  useEffect(() => {
    return onFilteredTasksChange(() => setTasks(getFilteredTasks()));
  }, []);

  return tasks;
};

export const useTasks = (): Task[] => {
  const [tasks, setTasks] = useState<Task[]>(getFilteredTasks);

  useEffect(() => {
    return onTasksChange(() => setTasks(getFilteredTasks()));
  }, []);

  return tasks;
};

export const useReleases = (): Release[] => getReleases();
export const useOrganizations = (): Organization[] => getOrganizations();
export const useSolutions = (): Solution[] => getSolutions();
export const useSolutionVersionsById = (): Record<string, SolutionVersion> => getSolutionVersionsById();

export const useTasksByRelease = (): Record<string, Task[]> => {
  const tasks = useFilteredTasks();

  return useMemo(
    () => groupBy(tasks, (task) => `${task.releaseId}`),
    [tasks],
  );
};

// Return tasks grouped by solution id
export const useTasksBySolution = (): Record<string, Task[]> => {
  const tasks = useFilteredTasks();

  return useMemo(
    () => groupBy(tasks, (task) => `${task.solutionId}`),
    [tasks],
  );
};

// Return tasks grouped by solution id and release id
export const useTasksBySolutionAndRelease = (): Record<string, Task[]> => {
  const tasks = useFilteredTasks();

  return useMemo(
    () => groupBy(tasks, (task) => `${task.solutionId}-${task.releaseId}`),
    [tasks],
  );
};

export const useTasksByOrganizationAndRelease = (): Record<string, Task[]> => {
  const tasks = useFilteredTasks();

  return useMemo(
    () => groupBy(tasks, (task) => `${task.organizationId}-${task.releaseId}`),
    [tasks],
  );
};

export const useUnassignedTasks = (): Task[] => {
  return useFilteredTasks().filter((task) => !task.releaseId);
};

export const useUnassignedTasksByOrganization = (): Record<string, Task[]> => {
  const tasks = useFilteredTasks().filter((task) => !task.releaseId);

  return useMemo(
    () => groupBy(tasks, (task) => `${task.organizationId}`),
    [tasks],
  );
};

export const useUnassignedTasksBySolution = (): Record<string, Task[]> => {
  const tasks = useFilteredTasks().filter((task) => !task.releaseId);

  return useMemo(
    () => groupBy(tasks, (task) => `${task.solutionId}`),
    [tasks],
  );
};

export const useTasksByOrganization = (): Record<string, Task[]> => {
  const tasks = useFilteredTasks();

  return useMemo(
    () => groupBy(tasks, (task) => `${task.organizationId}`),
    [tasks],
  );
}

export const useExpandedColumns = (): [
  number[],
  React.Dispatch<React.SetStateAction<number>>
] => {
  const [_columns, _setColumns] = useState<number[]>(getExpandedColumns());

  useEffect(
    () => onExpandedColumnsChange(_setColumns),
    [],
  );

  return [
    _columns,
    toggleColumn,
  ];
};

export const useExpandedRows = (): [
  number[],
  React.Dispatch<React.SetStateAction<number>>
] => {
  const [_rows, _setRows] = useState<number[]>(getExpandedRows());

  useEffect(
    () => onExpandedRowsChange(_setRows),
    [],
  );

  return [
    _rows,
    toggleRow,
  ];
};

export const useIsAuthenticated = (): boolean => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(getIsAuthenticated);

  useEffect(
    () => onIsAuthenticatedChange(setIsAuthenticated),
    [],
  );

  return isAuthenticated;
};

export const useSetIsAuthenticated = (): (isAuthenticated: boolean) => void => {
  return setIsAuthenticated;
};

export const useFilters = (): [
  Filter[],
  React.Dispatch<React.SetStateAction<Filter[]>>
] => {
  const [_filters, _setFilters] = useState<Filter[]>(getFilters());

  useEffect(
    () => onFiltersChange(_setFilters),
    [],
  );

  return [
    _filters,
    setFilters,
  ];
};

export const useDateRange = (): [
  DateRange,
  React.Dispatch<React.SetStateAction<DateRange>>
] => {
  const [_dateRange, _setDateRange] = useState<DateRange>(getDateRange());

  useEffect(
    () => onDateRangeChange(_setDateRange),
    [],
  );

  return [
    _dateRange,
    setDateRange,
  ];
};

export const useGroupBy = (): [
  'solution' | 'organization',
  React.Dispatch<React.SetStateAction<'solution' | 'organization'>>
] => {
  const [_groupBy, _setGroupBy] = useState<'solution' | 'organization'>(() => getGroupBy());

  useEffect(
    () => onGroupByChange(_setGroupBy),
    [],
  );

  return [
    _groupBy,
    setGroupBy,
  ];
};

export const useRowHeights = (): Record<string, number> => {
  const [_rowHeights, _setRowHeights] = useState<Record<string, number>>(getRowHeights());

  useEffect(
    () => onRowHeightsChange(_setRowHeights),
    [],
  );

  return _rowHeights;
};
